/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {downloadFile, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useRef, useState} from 'react';
import Invoice, {InvoiceFields} from '../../../models/invoiceModel';
import OrderItem, {OrderItemFields} from '../../../models/orderItemModel';
import {sendGaEvent, sendGaPageView, trackPlatformAction} from '../../../util/analytics';
import {onSetCrumbs} from '../../../util/events';
import {ActivateHandle, OrderItemErrors, VATType, VATValue} from '../../../util/interfaces';
import {getNavigationParam, setNavigationParam} from '../../../util/navigateUtil';
import {downloadInvoice} from '../../../util/requestSender';
import {createErrorMessage} from '../../../util/utils';
import {SplitVAT} from '../../../util/vatUtil';
import {AppContext} from '../../misc/appContext';
import CustomVat from '../../vat/customVAT/customVat';
import SplitVATController from '../../vat/splitVAT/splitVATController';
import EditInvoice from './editInvoice';

const ITEM_ERRORS: OrderItemErrors = {quantity: null, price: null, title: null};

export default function EditInvoiceController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const [itemErrors, setItemErrors] = useState<Record<string, OrderItemErrors>>({});
    const [invoice, setInvoice] = useState(new Invoice());
    const [loading, setLoading] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);
    const customRef = useRef<ActivateHandle>(null);
    const splitRef = useRef<ActivateHandle>(null);

    function onChangeInvoice(key: keyof InvoiceFields) {
        return function(value: any) {
            setInvoice(invoice.clone().setField(key, value));
            setHasChanges(true);
        }
    }

    function onChangeItemVat(item: OrderItem) {
        return function(value: VATValue) {
            onChangeItem(item, 'vat')(value);
        }
    }

    function onChangeItem(item: OrderItem, key: keyof OrderItemFields) {
        return function(value: any) {
            if (key === 'quantity' && value === '') value = 0;
            else if (typeof item.getField(key) === 'number') value = Number(value);
            if (key === 'vat' && value === -2)
                return customRef.current?.activate(invoice.country, item);
            item.setField(key, value);
            setInvoice(invoice.clone(false).updateItem(item));
            setHasChanges(true);
        }
    }

    function onChangeVAT(item: OrderItem) {
        return function(value: VATValue) {
            const {vat_type} = value;
            if (vat_type === VATType.CUSTOM)
                return customRef.current?.activate(invoice.country, item);
            else if (vat_type === VATType.SPLIT)
                return splitRef.current?.activate(invoice.country, item.id, item.getSplitVAT());
            else {
                item.vat = value;
                setInvoice(invoice.clone(false).updateItem(item));
                setHasChanges(true);
            }
        }
    }

    function onChangeSplitVAT(vats: Record<string, number>, _: string, productID: string) {
        let invoiceItem: OrderItem | null = null;
        for (const item of invoice.items) {
            if (item.id !== productID) continue;
            item.vat = SplitVAT(vats)
            invoiceItem = item;
            break;
        }
        if (invoiceItem) {
            setInvoice(invoice.clone(false).updateItem(invoiceItem));
            setHasChanges(true);
        }
    }

    function onSubmit() {
        sendGaEvent('Click', 'Factuur wijzigen', 'Opslaan');
        if (!validateValues()) return;
        setLoading(true);
        invoice.update((invoice: Invoice, success: boolean, data: ResponseData) => {
            const itemErrors: Record<string, OrderItemErrors> = {};
            for (const item of invoice.items) itemErrors[item.id] = ITEM_ERRORS;
            setItemErrors(itemErrors);
            setLoading(false);
            if (success) {
                trackPlatformAction('invoices.detail', 'edit_invoice');
                showSnackbar('De factuur is bijgewerkt');
                setNavigationParam(invoice);
                onConfirmSendInvoice(invoice);
                setHasChanges(false);
            } else if (!data.handled)
                showDialog(createErrorMessage(data.message ?? 'Het bijwerken van de factuur is niet gelukt.'),
                    'Bijwerken mislukt');
        });
    }

    function onConfirmSendInvoice(invoice: Invoice) {
        if (invoice.isSent)
            showDialog('Wil je de gewijzigde factuur direct verzenden?', 'Factuur verzenden',
                [
                    {label: 'Nee', primary: false, onClick: onBack},
                    {label: 'Ja, verzenden', onClick: onSendInvoice(invoice)},
                    {label: 'Factuur downloaden', onClick: onClickDownload(invoice), keepOpen: true},
                ]);
        else history.back();
    }

    function onBack() {
        history.back();
    }

    function onSendInvoice(invoice: Invoice) {
        return function() {
            setLoading(true);
            sendGaEvent('click', 'Factuur wijzigen', 'Factuur verzenden');
            invoice.send((invoice: Invoice, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    showSnackbar('De factuur is verzonden');
                    history.back();
                } else {
                    const {error_type} = data;
                    if (error_type === 'email_not_available')
                        showDialog(<label>Het originele e-mailadres van de klant is niet meer beschikbaar voor Bolmate.
                            We raden aan om de factuur te <span className='invoice-download-link'
                                                                onClick={onClickDownload(invoice)}>downloaden</span> en
                            deze via het Bol-platform te verzenden naar de klant.</label>, 'Verzenden mislukt');
                    else
                        showDialog(createErrorMessage(data.message ?? 'Het verzenden van de mail is niet gelukt.'),
                            'Verzenden mislukt');
                }
            });
        }
    }

    function onCancel() {
        sendGaEvent('Click', 'Factuur wijzigen', 'Annuleren');
        if (hasChanges) onPreventClose(() => {
            setHasChanges(false);
            window.setTimeout(() => {
                history.back();
            });
        });
        else history.back();
    }

    function onResetItemError(id: string, key: keyof OrderItemErrors) {
        return function() {
            const errors = Object.assign({}, itemErrors);
            if (errors.hasOwnProperty(id)) {
                errors[id][key] = null;
                setItemErrors(errors);
            }
        }
    }

    function onPreventClose(onDecline: VoidFunction) {
        showDialog('De factuur is gewijzigd, wil je de wijzigingen opslaan?', 'Factuur gewijzigd',
            [{label: 'Nee', onClick: onDecline, primary: false}, {label: 'Ja', onClick: onSubmit}])
    }

    function onClickDownload(invoice: Invoice) {
        return function() {
            if (!account) return;
            setLoading(true);
            downloadInvoice({
                invoice_id: invoice.id, account_id: account.id, callback: (data: ResponseData, response: Response) => {
                    setLoading(false);
                    if (data.status === 'success') {
                        // FILE RESPONSE
                        const header = response.headers.get('Content-Disposition');
                        let fileName = `${invoice.reference}.pdf`;
                        if (header)
                            fileName =
                                header.substring(header.indexOf('filename=') + 'filename='.length, header.length);
                        response.blob().then(blob => {
                            downloadFile(blob, fileName);
                        });
                    } else if (!data.handled)
                        showDialog(createErrorMessage(data.message ??
                            'Het downloaden van de factuur is niet gelukt.'), 'Downloaden mislukt');
                }
            });
        }
    }

    function validateValues(): boolean {
        const errors: Record<string, OrderItemErrors> = Object.assign({}, itemErrors);
        let valid: boolean = true;
        for (const item of invoice.items) {
            const {id, title, quantity, price} = item;
            if (!title.length) {
                errors[id].title = 'Vul de naam in';
                valid = false;
            }
            if (quantity < 1) {
                errors[id].quantity = 'Vul het aantal in';
                valid = false;
            }
            if (price < 1) {
                errors[id].price = 'Vul de prijs in';
                valid = false;
            }
        }
        if (!valid)
            this.setState({itemErrors: Object.assign({}, errors)});
        return valid;
    }

    function getInvoice() {
        if (!account) return window.history.back();
        const invoice: Invoice | string | any = getNavigationParam();
        if (invoice instanceof Invoice) setInitial(invoice);
        else if (typeof invoice === 'string')
            Invoice.get(invoice, account.id, (invoice: Invoice | null) => {
                if (!invoice) history.back();
                else {
                    setNavigationParam(invoice);
                    setInitial(invoice);
                }
            })
        else history.back();
    }

    function setInitial(invoice: Invoice) {
        sendGaPageView('/factuur-wijzigen');
        onSetCrumbs([{label: 'Facturen', route: '/facturen'}, {
            label: `Order ${invoice.orderId}`,
            route: '/factuur-details'
        }, {label: 'Factuur wijzigen'}]);
        const itemErrors: Record<string, OrderItemErrors> = {};
        for (const item of invoice.items) itemErrors[item.id] = ITEM_ERRORS;
        setInvoice(invoice);
        setItemErrors(itemErrors);
        setLoading(false);
    }

    useEffect(() => {
        getInvoice();
    }, []);

    return (
        <>
            <EditInvoice onChangeInvoice={onChangeInvoice} onChangeItem={onChangeItem} onSubmit={onSubmit}
                         onCancel={onCancel} onResetItemError={onResetItemError} onChangeVAT={onChangeVAT}
                         invoice={invoice} loading={loading} itemErrors={itemErrors}/>
            <CustomVat ref={customRef} onSubmit={onChangeItemVat}/>
            <SplitVATController onSubmit={onChangeSplitVAT} ref={splitRef}/>
        </>
    )
}